import React, { useCallback, useEffect, useRef } from 'react';
import { StyledChatComponent } from './style';
import ChatApi from '@/Services/Axios/Controllers/ChatController';
import { useFetch } from '@/Hooks/useFetch';
import { IChangeOfferParams, IChatUserDto, IMakeOfferDto, IMessageDto, IOfferDto, IPostMessageBody, MessageTypeEnum } from '@/Models/Chat';
import MessageApi from '@/Services/Axios/Controllers/MessageController';
import { ChatHeader } from '../ChatHeader';
import { ChatInput } from '../ChatInput';
import { RequestStatus } from '@/Services/Axios';
import numberService from '@/Services/NumberService';
import { ChatMessages } from '../ChatMessages';
import { YMaps } from 'react-yandex-maps';
import OfferApi from '@/Services/Axios/Controllers/OfferApi';
import { BiConversation } from 'react-icons/bi';
import { useTranslation } from 'next-i18next';
import { useIsMobile } from '@/Hooks/useIsMobile';
import { Offer } from '../Offer';
import { useOnFirebaseMessage } from '@/Hooks/useOnFirebaseMessage';
import { FirebaseMessageTypeEnum, typeMessageHandlerFunction } from '@/Services/Firebase';
import { useUserData } from '@/Hooks/useUserData';

type Props = {
  chatId: number | null;
  newChat?: boolean;
  demoChatData?: IChatUserDto;
  demoOfferData?: IOfferDto;
  handleCreateNewChat?: <T>(cb: (newChatId: number) => T) => Promise<void | T>;
  archive?: boolean;
}

const ChatComponent = ({ chatId, newChat, handleCreateNewChat, demoChatData, demoOfferData, archive = false }: Props) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const MessagesRef = useRef<HTMLDivElement>(null);
  const userData = useUserData()!;
  const [chatData, fetchChatData] = useFetch<IChatUserDto>();
  const [messageData, fetchMessageData, updateMessageData] = useFetch<IMessageDto[]>();
  const [postMessageData, postMessage] = useFetch<IPostMessageBody[]>();
  const [offerData, fetchOfferData, updateOfferData] = useFetch<IOfferDto>();

  const ChatLoading = chatData.status === RequestStatus.LOADING || offerData.status === RequestStatus.LOADING;


  // ------------------------ CHAT MESSAGE HANDLERS --------------------
  const onSendText = useCallback(async (text: string) => {
    if(newChat && !chatId && handleCreateNewChat) {
      handleCreateNewChat((createdChatId: number) => {
        postMessage(MessageApi.postMessage, { chatId: createdChatId, messageType: MessageTypeEnum.TEXT, text });
      })
    } else if(chatId) {
      postMessage(MessageApi.postMessage, { chatId, messageType: MessageTypeEnum.TEXT, text });
    }
  }, [chatId]);
  
  const onSendLocation = useCallback(async (lat: number, long: number) => {
    if(newChat && !chatId && handleCreateNewChat) {
      handleCreateNewChat((createdChatId: number) => {
        postMessage(MessageApi.postMessage, { chatId: createdChatId, messageType: MessageTypeEnum.LOCATION, text: `${lat},${long}` });
      })
    } else if(chatId) {
      postMessage(MessageApi.postMessage, { chatId, messageType: MessageTypeEnum.LOCATION, text: `${lat},${long}` });
    }
  }, [chatId]);

  const onMakeOffer = useCallback(async (productId: number, body: IMakeOfferDto) => {
    if(newChat && !chatId && handleCreateNewChat) {
      return handleCreateNewChat((createdChatId: number) => {
        return OfferApi.postOffer(productId, body);
      })
    };
    return OfferApi.postOffer(productId, body);
  }, [chatId]);
  
  const onChangeOfferStatus = useCallback(async (params: IChangeOfferParams) => {
    if(newChat && !chatId && handleCreateNewChat) {
      return handleCreateNewChat((createdChatId: number) => {
        return OfferApi.changeOffer(params);
      })
    }
    return OfferApi.changeOffer(params);
  }, [chatId]);

  // ------------------------- HELPER HANDLERS -------------------------
  const scrollToBottom = useCallback(() => {
    if(MessagesRef.current) {
      MessagesRef.current.scrollTop = MessagesRef.current.scrollHeight;
    }
  }, []);

  const reFetchMessages = useCallback(() => {
    if(chatId) {
      fetchMessageData(MessageApi.getMessagesByChatId, { chatId, size: numberService.maxInt32, lastId: Number.MAX_SAFE_INTEGER });
    }
  }, [chatId]);


  // ----------------------- NOTIFICATION HANDLERS --------------------------
  const updateMessagesState: typeMessageHandlerFunction = useCallback((payload) => {
    if(payload.data && payload.data.type === FirebaseMessageTypeEnum.CHAT_MESSAGE) {
      const recieved_msg = JSON.parse(payload.data.data) as Omit<IMessageDto, 'receiver'>;
      if(recieved_msg && recieved_msg.chatId === chatId && !messageData.data.some(m => m.id === recieved_msg.id)) {
        updateMessageData({
          data: [{...recieved_msg, receiver: userData?.id}, ...messageData.data]
        });
      }
    };
  }, [messageData, chatId]);
  
  const updateOfferState: typeMessageHandlerFunction = useCallback((payload) => {
    if(payload.data && (payload.data.type === FirebaseMessageTypeEnum.BUYER_CHANGED_OFFER || payload.data.type === FirebaseMessageTypeEnum.SELLER_CHANGED_OFFER)) {
      const recieved_Offer = JSON.parse(payload.data.data) as IOfferDto & { chatId: number };
      if(recieved_Offer && recieved_Offer.chatId === chatId && !messageData.data.some(m => m.id === recieved_Offer.id)) {
        updateOfferData({
          data: {...offerData.data, ...recieved_Offer}
        });
      }
    };
  }, [offerData, chatId]);

  // ----------------------------------- SIDE EFFECTS -------------------------
  useEffect(() => {
    if(chatId) {
      fetchChatData(ChatApi.getChatById, chatId);
      fetchMessageData(MessageApi.getMessagesByChatId, { chatId, size: numberService.maxInt32, lastId: Number.MAX_SAFE_INTEGER });
    }
  }, [chatId]);

  useEffect(() => {
    if(postMessageData.status === RequestStatus.SUCCESS && chatId) {
      fetchMessageData(MessageApi.getMessagesByChatId, { chatId, size: numberService.maxInt32, lastId: Number.MAX_SAFE_INTEGER });
    }
  }, [postMessageData]);
  
  useEffect(() => {
    if(chatData.status === RequestStatus.SUCCESS && chatId) {
      fetchOfferData(OfferApi.getOfferById, +chatData.data.offerId);
    }
  }, [chatData]);


  // -------------- FIREBASE MESSAGE HANDLERS -----------------
  useOnFirebaseMessage(updateMessagesState);
  useOnFirebaseMessage(updateOfferState);

  if(!chatId && !newChat) {
    return (
      <StyledChatComponent style={{ alignItems: 'center', justifyContent: 'center' }}>
        <BiConversation size={50} />
        <h3>{ t('choose_chat') }</h3>
      </StyledChatComponent>
    );
  }


  return (
    <YMaps>
      <StyledChatComponent className='chat-wrapper' isMobile={isMobile}>
        <div className='chat-wrapper__header'>
          <ChatHeader
            data={chatData.data || (newChat ? demoChatData : chatData.data)}
            loading={ChatLoading}
          />
        </div>
        <div className='chat-wrapper__main' ref={MessagesRef}>
          {
            !archive && (
              <div className='chat-wrapper__offer'>
                <Offer
                  key={chatId}
                  offerData={offerData.data || (newChat ? demoOfferData : offerData.data)}
                  chatData={chatData.data || (newChat ? demoChatData : chatData.data)}
                  loading={ChatLoading}
                  reFetchMessages={reFetchMessages}
                  // @ts-ignore TODO
                  makeOffer={onMakeOffer} changeOfferStatus={onChangeOfferStatus}
                />
              </div>
            )
          }
          <ChatMessages newChat={newChat} scrollToBottom={scrollToBottom} chatData={chatData.data} messages={messageData.data} loading={ChatLoading} />
        </div>
        {
          !archive && (
            <div className='chat-wrapper__input'>
              <ChatInput onSendLocation={onSendLocation} onSendText={onSendText} loading={postMessageData.status === RequestStatus.LOADING} />
            </div>
          )
        }
      </StyledChatComponent>
    </YMaps>
  )
}

export default ChatComponent;
